import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { DeployStepName, DeployStepStatus } from '@/types/board';
import { SarasaMonoK } from '@/styles/fonts';
import { OverlayContainer, ContentWrapper, StepperContainer } from './styles';
import { DEPLOY_STEPS, STEP_INDEX_MAP } from './constants';
import DeployStepItem from './DeployStepItem';

export interface DeployProgressStepperProps {
  isVisible: boolean;
  currentStep: DeployStepName | null;
  currentStatus: 'pending' | 'success' | 'failed' | 'warning';
  serverMessage: string;
  onCloseError?: () => void;
  advanceOnSuccess?: boolean;
  startFirstStepOnMount?: boolean;
  compactInProgress?: boolean;
  flushWrapper?: boolean;
}

// Step별 완료 상태 추적
interface StepState {
  status: DeployStepStatus;
  completedAt?: number; // 완료 시점의 경과 시간
}

const DeployProgressStepper: React.FC<DeployProgressStepperProps> = ({
  isVisible,
  currentStep,
  currentStatus,
  serverMessage,
  onCloseError,
  advanceOnSuccess = true,
  startFirstStepOnMount = true,
  compactInProgress = false,
  flushWrapper = false,
}) => {
  const { t } = useTranslation();
  // 각 step의 상태 추적 (경과 시간은 별도 상태로 관리)
  const [stepStates, setStepStates] = useState<
    Record<DeployStepName, StepState>
  >(() => ({
    codebuild_start: { status: 'pending' },
    security_scan: { status: 'pending' },
    build: { status: 'pending' },
    deploy: { status: 'pending' },
  }));

  // 현재 진행중인 step의 경과 시간 (별도 상태로 관리하여 다른 상태와 분리)
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // 타이머 ref
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 현재 서버에서 받은 step의 인덱스
  const currentServerStepIndex = useMemo(() => {
    if (!currentStep) return -1;
    return STEP_INDEX_MAP[currentStep] ?? -1;
  }, [currentStep]);

  // 현재 진행중인 step 인덱스 계산
  const activeStepIndex = useMemo(() => {
    // 서버에서 success/warning이 오면 해당 step은 완료, 다음 step이 진행중
    if (currentStatus === 'success' || currentStatus === 'warning') {
      return advanceOnSuccess ? currentServerStepIndex + 1 : currentServerStepIndex;
    }
    // 서버에서 failed가 오면 해당 step에서 에러
    if (currentStatus === 'failed') {
      return currentServerStepIndex < 0 ? 0 : currentServerStepIndex;
    }
    // pending이거나 아직 step이 없으면 첫번째 step이 진행중
    if (currentServerStepIndex < 0) {
      return startFirstStepOnMount ? 0 : -1; // codebuild_start가 진행중
    }
    return currentServerStepIndex;
  }, [
    advanceOnSuccess,
    currentServerStepIndex,
    currentStatus,
    startFirstStepOnMount,
  ]);

  // 타이머 정리
  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // 타이머 시작
  const startTimer = useCallback(() => {
    clearTimer();
    setElapsedSeconds(0);

    timerIntervalRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  }, [clearTimer]);

  // 서버 상태에 따라 stepStates 업데이트
  useEffect(() => {
    if (!isVisible) return;

    setStepStates((prev) => {
      const newStates = { ...prev };
      const failedStepIndex =
        currentStatus === 'failed' && currentServerStepIndex < 0
          ? DEPLOY_STEPS.findIndex(
              (step) => prev[step.name].status === 'in_progress',
            )
          : currentServerStepIndex;
      const errorStepIndex =
        failedStepIndex < 0 ? activeStepIndex : failedStepIndex;

      DEPLOY_STEPS.forEach((step, index) => {
        if (currentStatus === 'failed' && index === errorStepIndex) {
          newStates[step.name] = {
            ...newStates[step.name],
            status: 'error',
          };
        } else if (currentStatus === 'failed' && index < errorStepIndex) {
          if (newStates[step.name].status !== 'completed') {
            newStates[step.name] = {
              status: 'completed',
              completedAt: elapsedSeconds,
            };
          }
        } else if (index < activeStepIndex) {
          // 이전 단계들은 모두 completed
          if (newStates[step.name].status !== 'completed') {
            newStates[step.name] = {
              status: 'completed',
              completedAt: elapsedSeconds,
            };
          }
        } else if (index === activeStepIndex) {
          // 현재 단계
          if (
            (currentStatus === 'success' || currentStatus === 'warning') &&
            index === currentServerStepIndex
          ) {
            // 현재 step이 성공했고, 다음 step으로 넘어갈 때
            newStates[step.name] = {
              status: 'completed',
              completedAt: elapsedSeconds,
            };
          } else {
            // 진행중
            if (newStates[step.name].status !== 'in_progress') {
              newStates[step.name] = {
                ...newStates[step.name],
                status: 'in_progress',
              };
            }
          }
        } else {
          // 이후 단계들은 pending
          if (newStates[step.name].status !== 'pending') {
            newStates[step.name] = {
              status: 'pending',
            };
          }
        }
      });

      return newStates;
    });
  }, [
    isVisible,
    activeStepIndex,
    currentStatus,
    currentServerStepIndex,
    elapsedSeconds,
  ]);

  // activeStepIndex가 바뀔 때 타이머 리셋
  useEffect(() => {
    if (!isVisible) return;

    if (activeStepIndex >= 0 && activeStepIndex < DEPLOY_STEPS.length) {
      // 새로운 step으로 넘어가면 타이머 리셋
      startTimer();
    }
  }, [isVisible, activeStepIndex, startTimer]);

  // 컴포넌트가 보일 때 초기화
  useEffect(() => {
    if (isVisible) {
      // 처음 보일 때 첫 번째 step을 in_progress로 설정하고 타이머 시작
      setStepStates({
        codebuild_start: {
          status: startFirstStepOnMount ? 'in_progress' : 'pending',
        },
        security_scan: { status: 'pending' },
        build: { status: 'pending' },
        deploy: { status: 'pending' },
      });
      if (startFirstStepOnMount) {
        startTimer();
      }
    } else {
      // 숨겨질 때 초기화
      clearTimer();
      setElapsedSeconds(0);
      setStepStates({
        codebuild_start: { status: 'pending' },
        security_scan: { status: 'pending' },
        build: { status: 'pending' },
        deploy: { status: 'pending' },
      });
    }

    return () => clearTimer();
  }, [isVisible, startTimer, clearTimer, startFirstStepOnMount]);

  // 시간 포맷 (0m 05s)
  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = String(seconds % 60).padStart(2, '0');
    return `(${m}m ${s}s)`;
  };

  if (!isVisible) return null;

  const isFlush = flushWrapper || currentStatus === 'failed';

  return (
    <OverlayContainer className={SarasaMonoK.variable} isFlush={isFlush}>
      <ContentWrapper isFlush={isFlush}>
        <StepperContainer>
          {DEPLOY_STEPS.map((step, index) => {
            const state = stepStates[step.name];
            const isCurrentServerStep = index === currentServerStepIndex;

            // 메시지 결정
            let message = '';
            if (state.status === 'in_progress') {
              // 진행중일 때는 서버 메시지 표시 (현재 서버 step이면)
              if (isCurrentServerStep || index === activeStepIndex) {
                message = serverMessage;
              }
            } else if (state.status === 'completed') {
              // 완료시 고정 메시지
              message = t(step.successMessage);
            } else if (state.status === 'error') {
              // 에러시 서버 메시지
              message = t(serverMessage);
            }

            // 시간 표시
            const timeText =
              state.status === 'in_progress'
                ? formatTime(elapsedSeconds)
                : state.status === 'completed' &&
                  state.completedAt !== undefined
                ? 'Success'
                : state.status === 'error'
                ? 'Failed'
                : '';

            return (
              <DeployStepItem
                key={step.name}
                step={step}
                status={state.status}
                message={message}
                timeText={timeText}
                isFirst={index === 0}
                isLast={index === DEPLOY_STEPS.length - 1}
                compactInProgress={compactInProgress}
              />
            );
          })}
        </StepperContainer>
        {currentStatus === 'failed' && onCloseError && (
          <BoxButton
            variant="primary"
            size="medium"
            onClick={onCloseError}
            style={{ marginTop: 24, minWidth: 96 }}
          >
            {t('닫기')}
          </BoxButton>
        )}
      </ContentWrapper>
    </OverlayContainer>
  );
};

export default DeployProgressStepper;
